import type { SupStoredRewardTokenValuations } from "@/store/types";
import React, { useCallback } from "react";

import { SupAssetLabel, SupFieldControl, SupInput, SupText } from "@repo/core-ui";
import { SupMint } from "@repo/core-ui";

function formatValue(value: string) {
  return Intl.NumberFormat("en-US", {
    style: "currency",
    maximumFractionDigits: 0,
    currency: "USD",
    currencySign: undefined,
  }).format(parseInt(value));
}

interface LayoutSidebarAtomsRewardsTokenValuationProps {
  token: SupStoredRewardTokenValuations;
  onChange: (token: SupStoredRewardTokenValuations) => void;
}

export default function LayoutSidebarAtomsRewardsTokenValuation({ token, onChange }: LayoutSidebarAtomsRewardsTokenValuationProps) {
  const value = `${token.custom_price * token.total_supply}`;
  const defaultValue = `${token.price * token.total_supply}`;
  const handleValueChange = useCallback(
    (value: string | undefined) => {
      if (!value) {
        onChange({ ...token, custom_price: token.price });
      } else {
        const parsedValue = parseInt(value?.replace(/\D/gm, "") ?? "");
        if (!isNaN(parsedValue) && parsedValue > 0) {
          onChange({ ...token, custom_price: parsedValue / token.total_supply });
        }
      }
    },
    [onChange, token]
  );

  return (
    <div className="space-y-4 border-[0.5px] border-neutral-200 bg-neutral-25 p-4">
      <div className="flex w-full items-center justify-between">
        <SupAssetLabel
          className="w-fit items-center gap-2"
          label={
            <SupText variant="caption" weight="bold" className="text-neutral-700">
              {token.token_symbol}
            </SupText>
          }
        >
          <img src={token.token_image} className="h-4 w-4 rounded-full object-cover" alt={token.token_symbol} />
        </SupAssetLabel>
        <SupMint className="text-neutral-900" />
      </div>
      <SupFieldControl label="EXPECTED FDV" id={token.id} className="w-full" hint={`total supply: ${new Intl.NumberFormat().format(token.total_supply)}`}>
        <SupInput
          clearable
          inputProps={{
            onKeyDownCapture: e => !(["Backspace", "Delete", "ArrowRight", "ArrowLeft", "Shift", "Meta"].includes(e.key) || /\d/gm.test(e.key)) && e.preventDefault(),
          }}
          formatter={value => {
            const preparedValue = value.replace(/\D/gm, "");
            return /^0*$/g.test(preparedValue) ? value : formatValue(preparedValue);
          }}
          type="text"
          id="balance-search"
          fluid
          defaultValue={formatValue(value)}
          placeholder={formatValue(defaultValue)}
          className="w-full"
          onChange={e => handleValueChange(e)}
        />
      </SupFieldControl>
    </div>
  );
}
